import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { fmtBDT, fmtBDTShort, fmtDateShort, fmtNum } from "../lib/format";
import { TrendingUp, Trophy, Gamepad2, CreditCard, Target, ShieldCheck, Lock, Unlock, UserCog, FileSpreadsheet, FileText, Users, Calendar, Download, Loader2, Mail, Send, Eye, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const COLORS = ["#10B981", "#EF4444", "#F59E0B", "#3B82F6", "#8B5CF6", "#EC4899"];

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "permission-overrides", label: "Permission Overrides" },
  { id: "monthly-players", label: "Monthly Player Report" },
  { id: "agent-email", label: "Agent Monthly Email" },
];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

// Trigger a file download from a blob response
async function downloadBlob(url, params, filename) {
  const res = await api.get(url, { params, responseType: "blob" });
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^"]+)"?/.exec(cd);
  const fname = filename || (m && m[1]) || "report.bin";
  const blob = new Blob([res.data], { type: res.headers?.["content-type"] || "application/octet-stream" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function ExportButton({ icon: Icon, label, onClick, busy, color = "emerald", testid }) {
  const colorMap = {
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
    rose: "border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`h-9 px-3 inline-flex items-center gap-2 text-xs font-semibold rounded-sm border transition-colors disabled:opacity-60 disabled:cursor-wait ${colorMap[color]}`}
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Icon size={14} />}
      {label}
    </button>
  );
}

function PermissionOverridesTab() {
  const [data, setData] = useState({ events: [], summary: { total: 0, granted: 0, revoked: 0, by_type: {} } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | non_agent | any_method | granted | revoked
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const monthAgoIso = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const [start, setStart] = useState(monthAgoIso);
  const [end, setEnd] = useState(todayIso);
  const [exporting, setExporting] = useState({ xlsx: false, pdf: false });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "non_agent") params.type_filter = "withdraw_permission_updated";
      if (filter === "any_method") params.type_filter = "any_method_permission_updated";
      const res = await api.get("/reports/permission-overrides", { params });
      setData(res.data || { events: [], summary: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const handleExport = async (fmt) => {
    setExporting((s) => ({ ...s, [fmt]: true }));
    try {
      await downloadBlob("/reports/permission-overrides/export", { fmt, start, end });
    } catch (e) {
      console.error(e);
      alert("Export failed. Please try again.");
    } finally {
      setExporting((s) => ({ ...s, [fmt]: false }));
    }
  };

  const events = (data.events || []).filter((e) => {
    if (filter === "granted") return e.granted;
    if (filter === "revoked") return !e.granted;
    return true;
  });
  const s = data.summary || {};

  return (
    <div data-testid="permission-overrides-tab">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">
            <ShieldCheck size={13} className="text-blue-500" /> Total Events
          </div>
          <div className="font-display text-2xl font-bold tabular text-gray-900">{fmtNum(s.total || 0)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">All override toggles</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">
            <Unlock size={13} className="text-emerald-500" /> Granted
          </div>
          <div className="font-display text-2xl font-bold tabular text-emerald-600">{fmtNum(s.granted || 0)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Lock opened</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">
            <Lock size={13} className="text-rose-500" /> Revoked
          </div>
          <div className="font-display text-2xl font-bold tabular text-rose-600">{fmtNum(s.revoked || 0)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">Lock re-engaged</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-4">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">
            <UserCog size={13} className="text-amber-500" /> Categories
          </div>
          <div className="font-display text-base font-bold text-gray-900 tabular leading-tight">
            <span className="text-purple-600">{fmtNum(s.by_type?.withdraw_permission_updated || 0)}</span> non-agent · <span className="text-cyan-600">{fmtNum(s.by_type?.any_method_permission_updated || 0)}</span> any-cat
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Override type split</div>
        </div>
      </div>

      {/* Date range + Export buttons */}
      <div className="bg-white border border-gray-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">From</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="h-9 px-3 text-sm border border-gray-200 rounded-sm focus:outline-none focus:border-gray-900 tabular"
            data-testid="overrides-export-from"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">To</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="h-9 px-3 text-sm border border-gray-200 rounded-sm focus:outline-none focus:border-gray-900 tabular"
            data-testid="overrides-export-to"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton
            icon={FileSpreadsheet}
            label="Export Excel"
            onClick={() => handleExport("xlsx")}
            busy={exporting.xlsx}
            color="emerald"
            testid="overrides-export-xlsx"
          />
          <ExportButton
            icon={FileText}
            label="Export PDF"
            onClick={() => handleExport("pdf")}
            busy={exporting.pdf}
            color="rose"
            testid="overrides-export-pdf"
          />
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[
          { id: "all", label: "All" },
          { id: "non_agent", label: "Non-Agent (Wd lock)" },
          { id: "any_method", label: "Any-Category" },
          { id: "granted", label: "Granted only" },
          { id: "revoked", label: "Revoked only" },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            data-testid={`overrides-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`h-8 px-3 inline-flex items-center text-xs font-semibold rounded-sm border transition-colors ${
              filter === f.id
                ? "bg-gray-900 border-gray-900 text-white"
                : "bg-white border-gray-200 text-gray-700 hover:border-gray-400"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Events table */}
      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
          <ShieldCheck size={16} className="text-blue-500" />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Audit Trail</div>
            <div className="font-display text-lg font-bold">Withdrawal-Permission Overrides</div>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No override events recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="overrides-table">
              <thead className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">When</th>
                  <th className="px-4 py-3 text-left font-semibold">Player</th>
                  <th className="px-4 py-3 text-left font-semibold">Override</th>
                  <th className="px-4 py-3 text-left font-semibold">Action</th>
                  <th className="px-4 py-3 text-left font-semibold">Admin</th>
                  <th className="px-4 py-3 text-left font-semibold">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.map((e) => (
                  <tr key={e.id} data-testid={`override-row-${e.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-700 tabular text-[12px] whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{e.username || "—"}</div>
                      <div className="text-[11px] text-gray-500 font-mono">{(e.user_id || "").slice(-12)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold border ${
                        e.category === "any_method"
                          ? "border-cyan-200 bg-cyan-50 text-cyan-800"
                          : "border-purple-200 bg-purple-50 text-purple-800"
                      }`}>
                        {e.category === "any_method" ? "Any-Category" : "Non-Agent"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold border ${
                        e.granted
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : "border-rose-200 bg-rose-50 text-rose-800"
                      }`}>
                        {e.granted ? <><Unlock size={11} /> Granted</> : <><Lock size={11} /> Revoked</>}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 text-[12px]">{e.admin_email || "—"}</td>
                    <td className="px-4 py-3 text-gray-600 text-[12px] max-w-[260px] truncate" title={e.note}>
                      {e.note || <span className="text-gray-400 italic">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MonthlyPlayersTab() {
  const currentMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }, []);
  const [month, setMonth] = useState(currentMonth);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState({ xlsx: false, pdf: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/reports/players/monthly-summary", { params: { month } });
        if (!cancelled) setSummary(res.data || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [month]);

  const handleExport = async (fmt) => {
    setExporting((s) => ({ ...s, [fmt]: true }));
    try {
      await downloadBlob("/reports/players/monthly-export", { fmt, month });
    } catch (e) {
      console.error(e);
      alert("Export failed. Please try again.");
    } finally {
      setExporting((s) => ({ ...s, [fmt]: false }));
    }
  };

  // Generate last 12 months as options
  const monthOptions = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
      out.push({ value, label });
    }
    return out;
  }, []);

  return (
    <div data-testid="monthly-players-tab">
      {/* Month picker + export */}
      <div className="bg-white border border-gray-200 rounded-sm p-4 mb-5 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold flex items-center gap-1.5">
            <Calendar size={11} /> Reporting Month
          </label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="monthly-players-month-select"
            className="h-9 px-3 text-sm border border-gray-200 rounded-sm focus:outline-none focus:border-gray-900 bg-white"
          >
            {monthOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-gray-500 max-w-md leading-relaxed">
          Monthly report includes every player with deposit, withdrawal, or bet activity in <b className="text-gray-800">{month}</b>, plus all new sign-ups for the month.
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton
            icon={FileSpreadsheet}
            label="Download Excel"
            onClick={() => handleExport("xlsx")}
            busy={exporting.xlsx}
            color="emerald"
            testid="monthly-players-export-xlsx"
          />
          <ExportButton
            icon={FileText}
            label="Download PDF"
            onClick={() => handleExport("pdf")}
            busy={exporting.pdf}
            color="rose"
            testid="monthly-players-export-pdf"
          />
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <SummaryCard icon={Users} iconColor="text-blue-500" label="Active Players" value={loading ? "…" : fmtNum(summary?.active_players || 0)} sub="Deposits / withdraws / bets" />
        <SummaryCard icon={UserCog} iconColor="text-purple-500" label="New Signups" value={loading ? "…" : fmtNum(summary?.new_signups || 0)} sub="Registered this month" />
        <SummaryCard icon={TrendingUp} iconColor="text-emerald-500" label="Deposits (BDT)" value={loading ? "…" : fmtBDT(summary?.deposit_total || 0)} sub={`${fmtNum(summary?.deposit_count || 0)} approved txns`} />
        <SummaryCard icon={Download} iconColor="text-rose-500" label="Withdrawals (BDT)" value={loading ? "…" : fmtBDT(summary?.withdraw_total || 0)} sub={`${fmtNum(summary?.withdraw_count || 0)} completed txns`} />
      </div>

      {/* Net position card */}
      <div className="bg-white border border-gray-200 rounded-sm p-5">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck size={16} className="text-amber-500" />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Net Position</div>
            <div className="font-display text-lg font-bold">Deposit minus Withdrawal · {month}</div>
          </div>
        </div>
        {loading ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading summary…</div>
        ) : (
          <div className="flex items-baseline gap-3">
            <div className={`font-display text-4xl font-extrabold tabular ${
              (summary?.net_total || 0) >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}>
              {fmtBDT(summary?.net_total || 0)}
            </div>
            <div className="text-sm text-gray-500">
              {(summary?.net_total || 0) >= 0 ? "House profit" : "House loss"}
            </div>
          </div>
        )}
        <div className="mt-4 text-[12px] text-gray-500 leading-relaxed">
          The Excel export contains a full <b className="text-gray-700">Players</b> sheet with player ID, contact, balance, KYC status, first-deposit category, and per-player deposit/withdraw/bet totals for the month — perfect for compliance / AML monthly archives.
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, iconColor, label, value, sub }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-4">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1.5">
        <Icon size={13} className={iconColor} /> {label}
      </div>
      <div className="font-display text-2xl font-bold tabular text-gray-900">{value}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/overview");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const revenue = (data?.revenue_by_day || []).map((d) => ({ ...d, label: fmtDateShort(d.date) }));
  const outcomes = data?.bet_outcomes || [];
  const methods = data?.deposit_methods || [];
  const topPlayers = data?.top_players || [];
  const topGames = data?.top_games || [];

  if (loading) {
    return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;
  }

  return (
    <>
      {/* Revenue by day - Bar Chart */}
      <div className="bg-white border border-gray-200 rounded-sm p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Revenue Analysis</div>
            <div className="font-display text-xl font-bold text-gray-900">Last 14 days · Net Position</div>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-emerald-500 inline-block rounded-sm" /> Deposit</div>
            <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-red-500 inline-block rounded-sm" /> Withdraw</div>
            <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-blue-500 inline-block rounded-sm" /> Net</div>
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={revenue} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="label" fontSize={10} tick={{ fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis fontSize={10} tick={{ fill: "#9CA3AF" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtBDTShort(v)} />
              <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 2 }} formatter={(v) => fmtBDT(v)} />
              <Bar dataKey="deposit" fill="#10B981" radius={[2, 2, 0, 0]} />
              <Bar dataKey="withdraw" fill="#EF4444" radius={[2, 2, 0, 0]} />
              <Bar dataKey="net" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target size={16} className="text-amber-500" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Bet Outcomes</div>
              <div className="font-display text-lg font-bold">Win / Loss / Pending Distribution</div>
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={outcomes} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90} label={(e) => e.status}>
                  {outcomes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, border: "1px solid #E5E7EB", borderRadius: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs mt-2">
            {outcomes.map((o, i) => (
              <div key={o.status} className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="font-semibold capitalize">{o.status}:</span> <span className="tabular">{o.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={16} className="text-blue-500" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Deposit Methods</div>
              <div className="font-display text-lg font-bold">Approved Deposits by Channel</div>
            </div>
          </div>
          <div className="space-y-3">
            {methods.map((m, i) => {
              const max = Math.max(...methods.map((x) => x.volume));
              const pct = max > 0 ? (m.volume / max) * 100 : 0;
              return (
                <div key={m.method}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-semibold text-gray-900">{m.method}</span>
                    <span className="tabular text-gray-600">{fmtBDT(m.volume)} · {m.count} txn</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-sm overflow-hidden">
                    <div className="h-full rounded-sm" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                  </div>
                </div>
              );
            })}
            {methods.length === 0 && <div className="text-sm text-gray-500 text-center py-6">No approved deposits yet.</div>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Top 10 Players</div>
              <div className="font-display text-lg font-bold">By account balance</div>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {topPlayers.map((p, i) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-7 w-7 rounded-sm flex items-center justify-center text-[11px] font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{i + 1}</div>
                  <div>
                    <div className="text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-gray-500">{p.user_id}</div>
                  </div>
                </div>
                <div className="text-sm font-semibold tabular">{fmtBDT(p.balance)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center gap-2">
            <Gamepad2 size={16} className="text-blue-500" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Top 10 Games</div>
              <div className="font-display text-lg font-bold">By wager volume</div>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {topGames.map((g, i) => (
              <div key={g.name} className="px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-7 w-7 rounded-sm flex items-center justify-center text-[11px] font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{i + 1}</div>
                  <div>
                    <div className="text-sm font-semibold">{g.name}</div>
                    <div className="text-[11px] text-gray-500">{fmtNum(g.count)} bets</div>
                  </div>
                </div>
                <div className="text-sm font-semibold tabular">{fmtBDT(g.volume)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function AgentEmailTab() {
  const [agents, setAgents] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [busy, setBusy] = useState({ test: false, all: false, preview: false });
  const [status, setStatus] = useState(null); // {type:'success'|'error', text, detail}
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadAgents = async () => {
    try {
      const res = await api.get("/agents");
      const items = (res.data || []).filter((a) => (a.email || "").includes("@"));
      setAgents(items);
    } catch (e) {
      console.error(e);
    }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get("/reports/agents/email-monthly/history", { params: { limit: 24 } });
      setHistory(res.data?.runs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadAgents();
    loadHistory();
  }, []);

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) =>
      [a.name, a.username, a.email, a.mobile].some((v) => (v || "").toLowerCase().includes(q))
    );
  }, [agents, search]);

  const sendTest = async () => {
    if (!selectedAgent) {
      setStatus({ type: "error", text: "Please select an agent first." });
      return;
    }
    setBusy((b) => ({ ...b, test: true }));
    setStatus(null);
    try {
      const res = await api.post("/reports/agents/email-monthly", {
        month,
        only_agent_id: selectedAgent,
      });
      const d = res.data;
      if (d?.success) {
        setStatus({
          type: "success",
          text: `Test email sent to ${d.agent} for ${d.month}`,
          detail: d.message_id ? `Message ID: ${d.message_id}` : "",
        });
      } else {
        setStatus({ type: "error", text: "Email send returned no message id (provider issue)." });
      }
    } catch (e) {
      setStatus({ type: "error", text: e.response?.data?.detail || "Test send failed" });
    } finally {
      setBusy((b) => ({ ...b, test: false }));
    }
  };

  const sendAll = async () => {
    if (!window.confirm(`Send the ${month} statement to ALL active agents with email on file? This cannot be undone.`)) return;
    setBusy((b) => ({ ...b, all: true }));
    setStatus(null);
    try {
      const res = await api.post("/reports/agents/email-monthly", { month });
      const d = res.data;
      setStatus({
        type: "success",
        text: `Run complete for ${d.month}`,
        detail: `${d.sent} sent · ${d.failed} failed · ${d.skipped} skipped (of ${d.agents_total} agents)`,
      });
      loadHistory();
    } catch (e) {
      setStatus({ type: "error", text: e.response?.data?.detail || "Bulk run failed" });
    } finally {
      setBusy((b) => ({ ...b, all: false }));
    }
  };

  const previewPdf = async () => {
    if (!selectedAgent) {
      setStatus({ type: "error", text: "Please select an agent first." });
      return;
    }
    setBusy((b) => ({ ...b, preview: true }));
    setStatus(null);
    try {
      const agent = agents.find((a) => a.id === selectedAgent);
      const fname = `maxlive88_agent_${agent?.username || "preview"}_${month}.pdf`;
      await downloadBlob("/reports/agents/email-monthly/preview", { agent_id: selectedAgent, month, fmt: "pdf" }, fname);
    } catch (e) {
      setStatus({ type: "error", text: e.response?.data?.detail || "Preview failed" });
    } finally {
      setBusy((b) => ({ ...b, preview: false }));
    }
  };

  // Build month options: current month + last 12 past months
  const monthOptions = useMemo(() => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const baseLabel = d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
      const label = i === 0 ? `${baseLabel} (current)` : baseLabel;
      opts.push({ v, label });
    }
    return opts;
  }, []);

  return (
    <div data-testid="agent-email-tab" className="space-y-6">
      {/* Schedule banner */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 flex items-start gap-3">
        <Clock size={18} className="text-emerald-700 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-semibold text-emerald-900">Auto-delivery is active</div>
          <div className="text-[13px] text-emerald-800 mt-0.5 leading-relaxed">
            Every <b>1st of the month at 00:30 UTC</b>, every active agent (with an email on file) automatically receives a PDF transaction-history statement for the previous month. Use the controls below to send a test email, preview a statement, or trigger a one-off bulk run for any past month.
          </div>
        </div>
      </div>

      {/* Status alert */}
      {status && (
        <div
          data-testid="agent-email-status"
          className={`rounded-sm p-3 border flex items-start gap-2 ${
            status.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-rose-50 border-rose-200 text-rose-900"
          }`}
        >
          {status.type === "success" ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <div className="text-sm">
            <div className="font-semibold">{status.text}</div>
            {status.detail && <div className="text-[12px] opacity-80 mt-0.5">{status.detail}</div>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Controls card */}
        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-sm p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-emerald-600" />
            <div className="font-display text-lg font-bold">Send Statement</div>
          </div>

          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">Month</div>
            <select
              data-testid="agent-email-month-select"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {monthOptions.map((o) => (
                <option key={o.v} value={o.v}>{o.label} · {o.v}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-1">
              Agent <span className="text-gray-400 normal-case font-normal">({agents.length} eligible)</span>
            </div>
            <input
              type="text"
              data-testid="agent-email-search"
              placeholder="Filter by name / username / email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-9 px-3 text-sm border border-gray-300 rounded-sm bg-white mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <select
              data-testid="agent-email-agent-select"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              size={6}
              className="w-full text-sm border border-gray-300 rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {filteredAgents.length === 0 && <option value="" disabled>No agents match</option>}
              {filteredAgents.map((a) => (
                <option key={a.id} value={a.id}>
                  {(a.name || a.username)} · {a.email}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-col gap-2 pt-2">
            <button
              type="button"
              data-testid="agent-email-test-btn"
              onClick={sendTest}
              disabled={busy.test || !selectedAgent}
              className="h-10 inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy.test ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Send Test to Selected Agent
            </button>

            <button
              type="button"
              data-testid="agent-email-preview-btn"
              onClick={previewPdf}
              disabled={busy.preview || !selectedAgent}
              className="h-10 inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-sm border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy.preview ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
              Preview PDF (download)
            </button>

            <button
              type="button"
              data-testid="agent-email-sendall-btn"
              onClick={sendAll}
              disabled={busy.all}
              className="h-10 inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-sm border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {busy.all ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
              Send to ALL Agents (manual run)
            </button>
          </div>

          <div className="text-[11px] text-gray-500 leading-relaxed pt-2 border-t border-gray-100">
            Tip: use <b className="text-gray-700">Send Test</b> after editing the email template to verify formatting. The full bulk run is also fired automatically on the 1st of every month, so manual runs are only needed for past months or re-sends.
          </div>
        </div>

        {/* History card */}
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-blue-500" />
              <div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Audit Trail</div>
                <div className="font-display text-lg font-bold">Past Email Runs</div>
              </div>
            </div>
            <button
              type="button"
              onClick={loadHistory}
              data-testid="agent-email-history-refresh"
              className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
            >
              Refresh
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase tracking-widest text-gray-500">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Month</th>
                  <th className="text-left px-5 py-2.5 font-semibold">Started</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Agents</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Sent</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Failed</th>
                  <th className="text-right px-5 py-2.5 font-semibold">Skipped</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="agent-email-history-table">
                {loadingHistory && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-500">Loading…</td></tr>
                )}
                {!loadingHistory && history.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-500">No email runs yet. They'll appear here automatically on the 1st of each month, or after a manual run.</td></tr>
                )}
                {history.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3 font-semibold tabular">{r.month}</td>
                    <td className="px-5 py-3 text-gray-600 tabular text-xs">{fmtDateTime(r.started_at)}</td>
                    <td className="px-5 py-3 text-right tabular">{r.agents_total}</td>
                    <td className="px-5 py-3 text-right tabular text-emerald-700 font-semibold">{r.sent}</td>
                    <td className="px-5 py-3 text-right tabular text-rose-700 font-semibold">{r.failed}</td>
                    <td className="px-5 py-3 text-right tabular text-gray-500">{r.skipped}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("overview");

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports & Analytics"
        subtitle="Deep-dive into revenue, player behavior, and permission overrides"
      />

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            data-testid={`reports-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`h-10 px-4 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "permission-overrides" && <PermissionOverridesTab />}
      {tab === "monthly-players" && <MonthlyPlayersTab />}
      {tab === "agent-email" && <AgentEmailTab />}
    </div>
  );
}
